import { Component, OnInit } from '@angular/core';
import { BehaviorSubject, filter, forkJoin, Observable, of, switchMap, tap, map } from 'rxjs';
import { FiltersFormAnalytics, GazeMapsAnalytics } from 'src/app/core/models/analytics.model';
import { Entity } from 'src/app/core/models/entity.model';
import { Pagination, PaginationRequest } from 'src/app/core/models/pagination.model';
import { Team } from 'src/app/core/models/team.model';
import { User } from 'src/app/core/models/user.model';
import { ConfigService } from 'src/app/core/services/config.service';
import * as utils from 'src/app/shared/helpers/utils';
import { AnalyticsService } from 'src/app/core/services/analytics.service';
import { LicenseService } from 'src/app/core/services/license.service';
import { LoginService } from 'src/app/core/services/login.service';
import { Category, Package, PackageStatus } from 'src/app/core/models/package.model';
import { PackageType } from 'src/app/core/models/package-type.model';
import { CsvDataService } from 'src/app/core/services/export.service';
import { EntityService } from 'src/app/core/services/entity.service';

@Component({
  selector: 'hbo-analytics-vrresults',
  templateUrl: './analytics-vrresults.component.html',
  styleUrls: ['./analytics-vrresults.component.scss']
})
export class AnalyticsVrresultsComponent implements OnInit {
  user: User = utils?.localStorageGetItem('user');

  columns: string[] = [];
  sortableColumns: string[] = ['entity_id', 'opticalshop', 'identityCode', 'team', 'package_id', 'category', 'package_type'];
  // Columns that require sorting via the entities endpoint
  private entitySortColumns = new Set(['entity_id', 'identityCode', 'team']);

  // Maps entity column names to entity API field names
  private entitySortFieldMap: Record<string, string> = {
    entity_id: 'id',
    opticalshop: 'name',
    identityCode: 'identityCode',
    team: 'team'
  };

  // Maps package column names to package API field names
  private packageSortFieldMap: Record<string, string> = {
    package_id: 'id',
    opticalshop: 'assigned',
    package_type: 'name',
    category: 'category'
  };

  private readonly excludedVrResultPackageTypeIds = new Set([5, 6, 7, 8]);

  data$: GazeMapsAnalytics[] = [];

  progressiveCount: number = 0;
  officeCount: number = 0;
  singleVisionCount: number = 0;

  categories: Category[] = [{ id: 1, name: 'OPERATIVE' }, { id: 2, name: 'DEMO' }];

  pagination!: PaginationRequest;
  filters!: FiltersFormAnalytics;
  pageBefore!: number | null;
  entity!: Entity;

  refreshSearch$ = new BehaviorSubject<PaginationRequest | null>(null);
  entities_filter!: Entity[];
  packageTypes!: PackageType[];
  packageStatus!: PackageStatus[];
  status_filter!: string[];
  category_filter!: Category[];

  packages$ = new Observable<Pagination<Package>>;

  pagination$!: Pagination<Entity>;
  entitiesCollection$: Entity[] = [];
  packagesCollection: any[] = [];
  entities$ = this.refreshSearch$.pipe(
    filter(params => !!params),
    switchMap(params => {
      return of(this.pagination$)
    }),
    tap(result => {
      this.entitiesCollection$ = result.data.map((entity: Entity) => ({ ...entity }));
      this.pagination.entities = this.entitiesCollection$.map((entity:Entity)=> entity.id).join(',');

      // Fetch packages for the selected entities
      const entitiesFilter = '[' + this.pagination.entities + ']';
      this.licenseService.indexPackages({ entities: entitiesFilter, perPage: -1 }).subscribe(packagesResponse => {
        // Store packages data for mapping, excluding packages inactive before the start date
        const filteredPackages = packagesResponse.data.filter((pkg: any) =>
          this.isVrResultsPackage(pkg) &&
          (!this.filters?.startDate || !pkg.inactiveDate || pkg.inactiveDate >= this.filters.startDate!) &&
          this.hasTestStartDate(pkg) &&
          (!this.filters?.endDate || pkg.testStartDate <= this.filters.endDate!)
        );
        this.packagesCollection = filteredPackages;
        const packageIds = filteredPackages.map((pkg: any) => pkg.id).join(',');
        this.pagination['packages'] = packageIds;

        this.analyticsSvc.gazeMapsAnalytics({ ...this.effectiveFilters, ...this.pagination, } as Pagination<any>).subscribe((response: any) => {
          this.mapping(this.entitiesCollection$, this.filters, response);
        });
      });
    })
  );

  globalPagination!: PaginationRequest;
  downloadError: string | null = null;

  private readonly defaultPerPage = 10;

  private getDefaultStartDate(): string {
    const defaultStart = new Date();
    defaultStart.setMonth(defaultStart.getMonth() - 4);
    return defaultStart.toISOString().split('T')[0];
  }

  private get effectiveFilters(): FiltersFormAnalytics {
    if (this.filters?.startDate) return this.filters;
    return { ...this.filters, startDate: this.getDefaultStartDate() };
  }

  constructor(
    public configSvc: ConfigService,
    private analyticsSvc:AnalyticsService,
    private licenseService: LicenseService,
    private loginService: LoginService,
    private csvDataService: CsvDataService,
    private entityService: EntityService
  ) {
    this.user.childEntities = this.configSvc.currentConfigValue.childEntities;
    this.entity = this.user.entity;
    switch (this.user.roleId) {
      case 1:
        this.columns = ['entity_id', 'opticalshop', 'identityCode', 'team', 'package_id', 'package_type', 'category', 'progressive', 'office', 'singlevision', 'total'];
        break;
      case 2:
        this.columns = ['entity_id', 'opticalshop', 'identityCode', 'team', 'package_id', 'package_type', 'category', 'progressive', 'office', 'singlevision', 'total'];
        break;
      default:
        this.columns = ['entity_id', 'opticalshop', 'identityCode', 'team', 'package_id', 'package_type', 'category', 'progressive', 'office', 'singlevision', 'total'];
        break;
    }
  }

  ngOnInit() {
    // Initialize filters with default end date if not set
    if (!this.filters) {
      this.filters = {
        startDate: this.getDefaultStartDate(),
        endDate: new Date().toISOString().split('T')[0]
      };
    } else {
      if (!this.filters.startDate) {
        this.filters.startDate = this.getDefaultStartDate();
      }

      if (!this.filters.endDate) {
        this.filters.endDate = new Date().toISOString().split('T')[0];
      }
    }
    this.validateDownload();

    if (this.shouldDeferAdminRequests()) {
      this.setEmptyPackagesResult();
      return;
    }

    // this.searchEntities();
    this.licenseService.currentPackageConfigValue.pagination
      ? this.searchPackages(this.licenseService.currentPackageConfigValue.pagination)
      : null;
    const storagePackageFilter = JSON.parse(localStorage.getItem('packageFilter')!) as PaginationRequest
    if (storagePackageFilter) {
      // Parse filter_params if it's a string
      if (storagePackageFilter.filter_params && typeof storagePackageFilter.filter_params === 'string') {
        storagePackageFilter.filter_params = JSON.parse(storagePackageFilter.filter_params as unknown as string);
      }
      this.licenseService.setCurrentPackageParamConfig('pagination', storagePackageFilter);
      this.searchPackages(storagePackageFilter);
    } else {
      this.searchPackages();
    }
  }

  searchPackages(pagination?: PaginationRequest) {
    if (this.shouldDeferAdminRequests(pagination)) {
      this.setEmptyPackagesResult();
      return;
    }

    this.loginService.userStore.subscribe(async user => {

      this.entities_filter = user.childEntities!;
      //this.licenseService.setCurrentPackageParamConfig('partners',this.entities_filter);
      //get packages types
      if (!this.packageTypes) {
        !this.configSvc.currentConfigValue.packageTypes
          ? this.licenseService.getPackageTypes().subscribe(packageTypes => {
            this.packageTypes = packageTypes.data;
            this.configSvc.setConfigParamsValue('packageTypes', this.packageTypes);
            this.filterPolicy()
          })
          : (
            this.packageTypes = this.configSvc.currentConfigValue.packageTypes,
            this.filterPolicy()
          )
      }
      this.packageStatus = [
        {
          "id": 1,
          "name": "Assigned"
        },
        {
          "id": 2,
          "name": "Test"
        },
        {
          "id": 3,
          "name": "Active"
        },
        {
          "id": 4,
          "name": "Inactive Request"
        },
        {
          "id": 5,
          "name": "Inactive"
        },
        {
          "id": 6,
          "name": "In Upgrade"
        }
      ];
      this.category_filter = [{ id: 1, name: 'OPERATIVE' }, { id: 2, name: 'DEMO' }];
      const queryStringParams: PaginationRequest = {};
      const queryStringParamsArray: any[] = [];
      const searchInput = (pagination?.search ?? this.filters?.search ?? '').trim();
      pagination?.filter_params
        ? pagination.filter_params!['entities'] = this.licenseService.currentPackageConfigValue.shops_selected
        : null;
      //--------------------partners--------------------------------//
      let partners: number[];
      pagination?.filter_params && pagination?.filter_params!['entities'] && pagination?.filter_params!['entities'].length
        ? partners = pagination.filter_params!['entities'].map((entity: { internalCode: any; id: any; }) => Number(entity.id))
        : this.user.roleId === 1
          ? partners = []
          : partners = [Number(user.entity.id)].concat(this.user.childEntities!.filter((entity: Entity) => entity.entityTypeId === 3).map((entity: Entity) => entity.id));//including if distributor the headoffices
      pagination && pagination!.entities
        ? pagination!.entities = JSON.stringify(partners)
        : null;
      //--------------------partners--------------------------------//
      searchInput ? queryStringParams.search = searchInput : null;
      pagination?.sort
        ? queryStringParams.sort = pagination?.sort
        : null;
      pagination?.order
        ? queryStringParams.order = pagination?.order
        : null;
      let finalPagination: PaginationRequest;
      if (pagination?.filter_params) {
        Object.keys(pagination?.filter_params!).map(key => pagination[this.mapPaginationLabel(key)] = pagination?.filter_params![key]);
        finalPagination = JSON.parse(JSON.stringify(pagination));
        partners.length ? finalPagination.entities = JSON.stringify(partners) : null;
        delete finalPagination.filter_params;
        delete finalPagination.showFilters;
      } else {
        finalPagination = { 'entities': partners.length ? JSON.stringify(partners) : null };
      };
      !finalPagination
        ? finalPagination = { perPage: pagination?.perPage! || 0, page: pagination?.page || 1 }
        : (
          finalPagination.page = pagination?.page! || 1,
          finalPagination.perPage = pagination?.perPage! || 0
        )
      /*if (!pagination || !pagination.filter_params || !pagination.filter_params['entities'] || !pagination.filter_params['entities'].length) {
        //finalPagination.entities = `[${this.user.entity.id}]`;
        let entitiesIds: number[] = [];
        this.user.childEntities.length ? entitiesIds = this.user.childEntities.map(a => a.id) : null;
        entitiesIds.push(this.user.entity.id);
        finalPagination.entities =  JSON.stringify(partners)
      }*/
      finalPagination.sort = pagination?.sort;
      finalPagination.order = pagination?.order;

      if (!finalPagination.sort && !finalPagination.order) {
        finalPagination.sort = 'id';
        finalPagination.order = 'desc';
      }

      searchInput ? finalPagination.search = searchInput : delete finalPagination.search;

      this.configSvc.currentConfigValue.packageEntitiesSelectedApplied
        ? finalPagination.entities = JSON.stringify(this.configSvc.currentConfigValue.packageEntitiesSelectedApplied.map((item: any) => item.id))
        : null;

      const nextGlobalPagination: PaginationRequest = { ...finalPagination };
      if (this.filters?.withoutActivity) {
        delete nextGlobalPagination.sort;
        delete nextGlobalPagination.order;
      }
      this.globalPagination = nextGlobalPagination;

      if (this.filters?.entities) {
        finalPagination.entities = JSON.stringify(this.filters.entities.split(',').map(Number));
      }

      if (this.filters?.category) {
        finalPagination['category'] = this.filters.category;
      } else {
        delete finalPagination['category'];
      }

      const requestedPerPage = Math.max(pagination?.perPage || this.defaultPerPage, 1);
      const requestedPage = pagination?.page || 1;

      const runPackagesPipeline = async () => {
        if (!this.filters?.withoutActivity) {
          this.loadPackagesWithAnalyticsPagination(finalPagination, requestedPage, requestedPerPage);
          return;
        }

        // When withoutActivity is true, fetch all packages without sort (API doesn't support it)
        const packagesRequestPagination = { ...finalPagination, perPage: -1, page: 1, sort: undefined, order: undefined };
        delete packagesRequestPagination.sort;
        delete packagesRequestPagination.order;

        (await this.licenseService.indexPackages(packagesRequestPagination ?? { order: 'desc' })).subscribe(packages => {
          const filteredPackagesData = packages.data.filter((pkg: Package) =>
            this.isVrResultsPackage(pkg) &&
            (!this.filters?.startDate || !pkg.inactiveDate || pkg.inactiveDate >= this.filters.startDate!) &&
            this.hasTestStartDate(pkg) &&
            (!this.filters?.endDate || pkg.testStartDate <= this.filters.endDate!)
          );
          this.packagesCollection = filteredPackagesData;
          const packageIds = filteredPackagesData.map((pkg: Package) => pkg.id).join(',');

          this.analyticsSvc.gazeMapsAnalytics({ ...this.effectiveFilters, packages: packageIds } as unknown as Pagination<any>).subscribe((response: any) => {
            this.mapping(this.user.childEntities!, this.filters, response);

            let rows = [...this.data$];

            if (this.filters?.withoutActivity) {
              rows = rows.filter(item => item.total === 0);
            }

            // Apply sorting on mapped analytics rows before pagination
            const sortColumn = pagination?.sort || queryStringParams.sort;
            const sortOrder = pagination?.order || queryStringParams.order || '';
            rows = this.sortAnalyticsRows(rows, sortColumn, sortOrder);

            const filteredTotal = rows.length;

            if (this.filters?.withoutActivity) {
              const start = (requestedPage - 1) * requestedPerPage;
              rows = rows.slice(start, start + requestedPerPage);
            }

            this.data$ = rows;

            const paginationResult = {
              total: this.filters?.withoutActivity ? filteredTotal : packages.total,
              perPage: this.filters?.withoutActivity ? requestedPerPage : Number(packages.perPage) || requestedPerPage,
              currentPage: this.filters?.withoutActivity ? requestedPage : (packages.currentPage || requestedPage),
              lastPage: this.filters?.withoutActivity
                ? Math.ceil(filteredTotal / requestedPerPage)
                : (packages.lastPage || 1),
              from: this.filters?.withoutActivity ? ((requestedPage - 1) * requestedPerPage) + 1 : (packages.from || 0),
              to: this.filters?.withoutActivity ? (((requestedPage - 1) * requestedPerPage) + this.data$.length) : (packages.to || 0),
              data: packages.data,
              sort: pagination?.sort || queryStringParams.sort,
              search: searchInput || queryStringParams.search,
              order: pagination?.order || queryStringParams.order,
              showFilters: pagination?.showFilters || false,
              selectorEntitiesSearch: pagination?.selectorEntitiesSearch || '',
              filter_params: pagination?.filter_params || queryStringParams.filter_params,
              entities: pagination?.entities || undefined
            };
            this.packages$ = of(paginationResult);
          });
        });
      };

      if (searchInput) {
        this.searchEntityIdsByTerm(searchInput, requestedPerPage, finalPagination.entities).subscribe(entityIds => {
          if (!entityIds.length) {
            this.emitPackagesPage([], finalPagination, requestedPage, requestedPerPage);
            return;
          }

          finalPagination.entities = JSON.stringify(entityIds);
          delete finalPagination.search;
          runPackagesPipeline();
        });
        return;
      }

      runPackagesPipeline();
    });


  }

  mappingQueryLabels: any = { category: 'category', packageType: 'type', status: 'status', packageInactive: 'inactive', entities: 'entities' }
  mapPaginationLabel(_key: string) {
    return this.mappingQueryLabels[_key]
  }

  searchEntities(pagination?: PaginationRequest) {
    this.pagination = pagination!;
    if (!this.pagination) this.pagination = { order: 'asc', page: 1, perPage: 10 }
    if (this.filters?.entities) this.pagination.entities = this.filters.entities!;
    else this.pagination.entities = null;
    // if entities filtered change reinit pagination to page 1
    if (this.pageBefore === this.pagination?.page!) this.pagination.page = 1
    else this.pageBefore = this.pagination?.page!;

    const groupedOpticalShopEntities = this.configSvc.currentConfigValue.childEntities!.reduce((acc, entity) => {
      const typeId = entity.entityTypeId;
      if (!acc[typeId]) {
        acc[typeId] = [];
      }
      acc[typeId].push(entity);
      return acc;
    }, {} as Record<string | number, Entity[]>);

    const ini = this.pagination?.perPage! * (this.pagination?.page! - 1);
    const end = ini + Number(this.pagination?.perPage!);
    let opticalShops: Entity[] = groupedOpticalShopEntities[4].sort((a, b) => a.id - b.id);

    //filtering entities
    if (this.pagination.entities) {
      opticalShops = opticalShops.filter((entity: Entity) => this.pagination?.entities!.split(',').map(Number).includes(entity.id));
    }
    //filtering team
    if (this.filters?.team) {
      opticalShops = opticalShops.filter((entity: Entity) => entity.team?.id === this.filters?.team);
    }
    //page entities
    let partialCollection$ = opticalShops.slice(ini, end);

    this.pagination$ = {
      total: opticalShops.length,
      perPage: this.pagination?.perPage!,
      currentPage: this.pagination?.page!,
      lastPage: Math.ceil(opticalShops.length / this.pagination?.perPage!),
      from: ini,
      to: end,
      search: this.pagination?.search!,
      entities: this.pagination?.entities!,
      data: partialCollection$
    }

    this.refreshSearch$.next(pagination ?? { order: 'asc', page: 1});
  }

  reload = (e: FiltersFormAnalytics) => {
    this.filters = e;
    this.validateDownload();

    const resetPagination: PaginationRequest = {
      ...(this.globalPagination || {}),
      page: 1,
      perPage: this.globalPagination?.perPage || this.defaultPerPage,
      search: (e as any)?.search || ''
    };

    if (e?.withoutActivity) {
      delete resetPagination.sort;
      delete resetPagination.order;
    }

    this.globalPagination = resetPagination;
    this.licenseService.setCurrentPackageParamConfig('pagination', resetPagination);
    localStorage.setItem('packageFilter', JSON.stringify(resetPagination));

    this.searchPackages(resetPagination);
  }

  onSortChange = (e: { sort: string; order: string }) => {
    if (this.shouldDeferAdminRequests(this.globalPagination)) {
      this.setEmptyPackagesResult();
      return;
    }

    if (this.filters?.withoutActivity) {
      // Keep sort transient for this request to avoid persisting unsupported package sort in packageFilter.
      this.searchPackages({ ...this.globalPagination, page: 1, sort: e.sort, order: e.order });
      return;
    }

    // Update globalPagination with new sort and order
    this.globalPagination.sort = e.sort; // Store UI column name
    this.globalPagination.order = e.order;
    // Request first page with new sort
    this.searchPackages({ ...this.globalPagination, page: 1 });
  }

  private shouldDeferAdminRequests(pagination?: PaginationRequest): boolean {
    const searchTerm = (pagination?.search ?? this.filters?.search ?? '').trim();
    return this.user.roleId === 1 && !this.filters?.entities && !searchTerm;
  }

  private setEmptyPackagesResult(): void {
    this.data$ = [];
    this.packagesCollection = [];
    this.packages$ = of({
      total: 0,
      perPage: this.defaultPerPage,
      currentPage: 1,
      lastPage: 1,
      from: 0,
      to: 0,
      data: []
    } as Pagination<Package>);
  }

  downloadSummary() {
    if (this.downloadError) return;

    const perPage = 500;
    const pagination: any = { ...this.globalPagination, perPage, page: 1 };

    if (this.filters?.entities) {
      pagination.entities = JSON.stringify(this.filters.entities.split(',').map(Number));
    }
    if (this.filters?.category) {
      pagination.category = this.filters.category;
    } else {
      delete pagination.category;
    }

    const rows: any[] = [];
    this.iterateSummaryPackages(pagination, perPage, 1, 0, rows);
  }

  iterateSummaryPackages = (pagination: any, perPage: number, page: number, lastPage: number, rows: any[]) => {
    pagination.page = page;
    this.licenseService.indexPackages(pagination).subscribe(packages => {
      lastPage = packages.lastPage;
      const filteredData = packages.data.filter((pkg: Package) =>
        this.isVrResultsPackage(pkg) &&
        (!this.filters?.startDate || !pkg.inactiveDate || pkg.inactiveDate >= this.filters.startDate!) &&
        this.hasTestStartDate(pkg) &&
        (!this.filters?.endDate || pkg.testStartDate <= this.filters.endDate!)
      );

      const chunkSize = 30;
      const ids = filteredData.map((pkg: Package) => pkg.id);
      const chunks: number[][] = [];
      for (let i = 0; i < ids.length; i += chunkSize) {
        chunks.push(ids.slice(i, i + chunkSize));
      }

      const entitiesById = new Map(this.user.childEntities!.map((e: Entity) => [e.id, e]));
      const packagesById = new Map(filteredData.map((pkg: Package) => [pkg.id, pkg]));

      const analyticsObservables = chunks.map(chunk => {
        return this.analyticsSvc.gazeMapsAnalytics({ ...this.effectiveFilters, packages: chunk.join(',') } as unknown as Pagination<any>);
      });

      forkJoin(analyticsObservables.length ? analyticsObservables : [of([])]).subscribe((responses: any[]) => {
        const countsByPkg = new Map(
          (responses as any[][]).flatMap(r => r).map((item: any) => [item.packageId, item])
        );

        for (const pkg of filteredData) {
          const entity = entitiesById.get(pkg.assignedEntityId);
          const counts = countsByPkg.get(pkg.id);
          const progressive = counts?.progressive ?? 0;
          const office = counts?.office ?? 0;
          const singleVision = counts?.singleVision ?? 0;
          const total = counts?.total ?? 0;

          if (this.filters?.withoutActivity && total !== 0) {
            continue;
          }

          rows.push({
            Entity_Id: entity?.id ?? '',
            Optical_Shop: (entity as any)?.name ?? '',
            Identity_Code: (entity as any)?.identityCode ?? '',
            Team: (entity as any)?.team?.name ?? '',
            Package_Id: pkg.id ?? '',
            Package_Type: pkg.packageType?.name ?? '',
            Category: pkg.category?.name ?? '',
            Progressive: progressive,
            Office: office,
            Single_Vision: singleVision,
            Total: total
          });
        }

        if (page >= lastPage) {
          this.csvDataService.downloadFile(
            rows,
            'vr-results-summary',
            ['Entity_Id', 'Optical_Shop', 'Identity_Code', 'Team', 'Package_Id', 'Package_Type', 'Category', 'Progressive', 'Office', 'Single_Vision', 'Total']
          );
        } else {
          this.iterateSummaryPackages(pagination, perPage, page + 1, lastPage, rows);
        }
      });
    });
  }

  private validateDownload(): void {
    this.downloadError = null;

    if (this.user.roleId === 1 && !this.filters?.entities) {
      this.downloadError = 'COMMON.CSV_SINGLE_DISTRIBUTOR_MSG';
      return;
    }

    if (this.filters?.entities) {
      const selectedIds = this.filters.entities.split(',').map(Number);
      const allEntities = this.user.childEntities!;
      const distributorIds = new Set<number>();

      for (const id of selectedIds) {
        const distributorId = this.getDistributorId(id, allEntities);
        if (distributorId !== null) distributorIds.add(distributorId);
      }

      if (distributorIds.size > 1) {
        this.downloadError = 'COMMON.CSV_SINGLE_DISTRIBUTOR_MSG';
      }
    }
  }

  private getDistributorId(entityId: number, allEntities: Entity[]): number | null {
    const entity = allEntities.find(e => e.id === entityId);
    if (!entity) return null;
    if (entity.entityTypeId === 2) return entity.id;
    if (!entity.parentEntityId) return null;
    return this.getDistributorId(entity.parentEntityId, allEntities);
  }

  private searchEntityIdsByTerm(search: string, perPage: number, entitiesFilter?: string | null): Observable<number[]> {
    const pagination: PaginationRequest = {
      page: 1,
      perPage,
      sort: 'id',
      order: 'desc',
      search
    };

    if (entitiesFilter) {
      pagination.entities = this.normalizeEntitiesFilter(entitiesFilter);
    }

    return this.entityService.index(pagination).pipe(
      map((result: Pagination<Entity>) =>
        (result.data || [])
          .filter((entity: Entity) => entity.entityTypeId === 4)
          .map((entity: Entity) => entity.id)
      )
    );
  }

  private normalizeEntitiesFilter(entities: string): string {
    const trimmed = entities.trim();
    if (!trimmed) return '';

    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed.join(',') : trimmed;
      } catch {
        return trimmed.replace(/\[|\]|\s/g, '');
      }
    }

    return trimmed;
  }

  private loadPackagesWithAnalyticsPagination = (pagination: any, requestedPage: number, requestedPerPage: number): void => {
    const fetchPagination = {
      ...pagination,
      page: 1,
      perPage: 50,
      sort: undefined,
      order: undefined
    };

    this.iteratePackagesForPagination(fetchPagination, 1, [], (allPackages: Package[]) => {
      const filtered = allPackages.filter((pkg: Package) =>
        this.isVrResultsPackage(pkg) &&
        (!this.filters?.startDate || !pkg.inactiveDate || pkg.inactiveDate >= this.filters.startDate!) &&
        this.hasTestStartDate(pkg) &&
        (!this.filters?.endDate || pkg.testStartDate <= this.filters.endDate!)
      );

      const sorted = this.sortPackages(filtered, pagination?.sort, pagination?.order);
      this.emitPackagesPage(sorted, pagination, requestedPage, requestedPerPage);
    });
  }

  private iteratePackagesForPagination = (pagination: any, page: number, collected: Package[], onComplete: (packages: Package[]) => void): void => {
    const requestPagination = { ...pagination, page };
    (this.licenseService.indexPackages(requestPagination) as any).subscribe((packages: any) => {
      const currentData: Package[] = packages?.data || [];
      const allCollected = collected.concat(currentData);
      const lastPage = packages?.lastPage || 1;

      if (page >= lastPage || !currentData.length) {
        onComplete(allCollected);
        return;
      }

      this.iteratePackagesForPagination(pagination, page + 1, allCollected, onComplete);
    });
  }

  private sortPackages = (packages: Package[], sortColumn?: string, order?: string): Package[] => {
    if (!sortColumn) {
      return [...packages].sort((a: Package, b: Package) => b.id - a.id);
    }

    const multiplier = (order || 'asc').toLowerCase() === 'desc' ? -1 : 1;
    const entitiesById = new Map(this.user.childEntities!.map((entity: Entity) => [entity.id, entity]));

    return [...packages].sort((a: Package, b: Package) => {
      const left = this.getSortValue(a, sortColumn, entitiesById);
      const right = this.getSortValue(b, sortColumn, entitiesById);

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * multiplier;
      }

      return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
    });
  }

  private getSortValue = (pkg: Package, sortColumn: string, entitiesById: Map<number, Entity>): string | number => {
    const entity = entitiesById.get(pkg.assignedEntityId);

    switch (sortColumn) {
      case 'entity_id':
        return entity?.id ?? 0;
      case 'opticalshop':
        return entity?.name ?? '';
      case 'identityCode':
        return entity?.identityCode ?? '';
      case 'team':
        return entity?.team?.name ?? '';
      case 'package_id':
        return pkg.id ?? 0;
      case 'package_type':
        return pkg.packageType?.name ?? '';
      case 'category':
        return pkg.category?.name ?? '';
      default:
        return pkg.id ?? 0;
    }
  }

  private emitPackagesPage = (allPackages: Package[], pagination: any, requestedPage: number, requestedPerPage: number): void => {
    const total = allPackages.length;
    const pageStart = (requestedPage - 1) * requestedPerPage;
    const pageEnd = pageStart + requestedPerPage;
    const pagePackages = allPackages.slice(pageStart, pageEnd);

    const paginationResult = {
      total,
      perPage: requestedPerPage,
      currentPage: requestedPage,
      lastPage: Math.ceil(total / requestedPerPage) || 1,
      from: total ? pageStart + 1 : 0,
      to: total ? pageStart + pagePackages.length : 0,
      data: pagePackages,
      sort: pagination?.sort,
      search: pagination?.search,
      order: pagination?.order,
      showFilters: pagination?.showFilters || false,
      selectorEntitiesSearch: pagination?.selectorEntitiesSearch || '',
      filter_params: pagination?.filter_params,
      entities: pagination?.entities || undefined
    };

    this.packagesCollection = pagePackages;

    if (!pagePackages.length) {
      this.data$ = [];
      this.packages$ = of(paginationResult);
      return;
    }

    const packageIds = pagePackages.map((pkg: Package) => pkg.id).join(',');
    this.analyticsSvc.gazeMapsAnalytics({ ...this.effectiveFilters, packages: packageIds } as unknown as Pagination<any>).subscribe((response: any) => {
      this.mapping(this.user.childEntities!, this.filters, response);
      this.packages$ = of(paginationResult);
    });
  }

  private isVrResultsPackage(pkg: any): boolean {
    const packageTypeId = pkg?.packageType?.id || pkg?.type?.id;
    return !this.excludedVrResultPackageTypeIds.has(Number(packageTypeId));
  }

  private hasTestStartDate(pkg: any): boolean {
    return pkg?.testStartDate != null;
  }

  mapping = (_entities: Entity[], _form: FiltersFormAnalytics, _values: any[]) => {
    const entitiesById = new Map(_entities.map(entity => [entity.id, entity]));
    const packagesById = new Map(this.packagesCollection.map(pkg => [pkg.id, pkg]));

    this.data$ = _values.map(item => {
      const pkg = packagesById.get(item.packageId);
      const entity = pkg ? entitiesById.get(pkg.assignedEntityId) : null;

      return {
        id: entity?.id ?? 0,
        name: entity?.name ?? '-------',
        code: entity?.identityCode ? String(entity?.identityCode) : '',
        team: entity?.team?.name ?? '-',
        packageId: item.packageId,
        packageType: pkg?.packageType?.name ?? '',
        packageCategory: pkg?.category.name,
        progressive: item.progressive ?? 0,
        office: item.office ?? 0,
        singleVision: item.singleVision ?? 0,
        total: item.total ?? 0
      };
    });
  }
  progressive = (_entity: number) => { 
    this.progressiveCount = 33; // Math.floor(Math.random() * 1000);
    return this.progressiveCount
  }
  office = (_entity: number) => {
    this.officeCount = 33; // Math.floor(Math.random() * 1000);
    return this.officeCount
  }
  singleVision = (_entity: number) => {
    this.singleVisionCount = 33; // Math.floor(Math.random() * 1000);
    return this.singleVisionCount
  }
  total = () => {
    return this.progressiveCount + this.officeCount + this.singleVisionCount
  }

  filterPackageTypesByPolicy(entityId: number) {
    let errorPolicy!: string;
    this.licenseService.showPolicy(entityId).subscribe({
      next: policy => {
        errorPolicy = policy.error?.error;
        if (errorPolicy) {
          switch (errorPolicy) {
            case 'Policy not found':
              this.packageTypes = this.packageTypes.filter(packageType => !packageType.private)
              break;
            default:
              break;
          }
        } else {
          if (policy.packagePolicies) {
            this.packageTypes = this.packageTypes.filter(packageType => policy.packagePolicies.some(packagePolicy => packagePolicy.packageTypeId === packageType.id));
          }
        }
      },
      error: () => this.packageTypes = this.packageTypes.filter(packageType => !packageType.private)
    });
  }

  filterPolicy = () => {
    switch (this.entity.entityTypeId) {
      case 3:
        this.filterPackageTypesByPolicy(this.entity.parentEntityId);
        break;
      default:
        this.filterPackageTypesByPolicy(this.entity.id);
        break
    }
  }

  private sortAnalyticsRows = (rows: GazeMapsAnalytics[], sortColumn?: string, order?: string): GazeMapsAnalytics[] => {
    const normalizedSort = (sortColumn || '').trim();
    const direction = (order || '').toLowerCase();
    
    if (!normalizedSort || !direction) {
      return [...rows];
    }

    const multiplier = direction === 'desc' ? -1 : 1;

    return [...rows].sort((a: any, b: any) => {
      const left = this.getAnalyticsSortValue(a, normalizedSort);
      const right = this.getAnalyticsSortValue(b, normalizedSort);

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * multiplier;
      }

      return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
    });
  }

  private getAnalyticsSortValue = (row: any, sortColumn: string): string | number => {
    switch (sortColumn) {
      case 'entity_id':
        return row?.id ?? 0;
      case 'opticalshop':
        return row?.name ?? '';
      case 'identityCode':
        return row?.code ?? '';
      case 'team':
        return row?.team ?? '';
      case 'package_id':
      case 'id':
        return row?.packageId ?? 0;
      case 'package_type':
      case 'name':
        return row?.packageType ?? '';
      case 'category':
        return row?.packageCategory ?? '';
      default:
        return row?.packageId ?? 0;
    }
  }

}
